import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SearchContainer, SEARCH_LISTBOX_ID } from './search/SearchContainer';
import { getOptionId } from './search/dropdown/DropdownItem';
import {
    addSearchQuery,
    searchFieldUpdated,
    setSearchPanelOpen,
} from '@/actions/searchActions';
import { useDispatch, useSelector } from '@/state/store';
import useSearchBarFunctions from '@/components/avgrensning/search/dropdown/useSearchBarFunctions';
import {
    useRecentSearches,
    useSearchResult,
} from '@/components/avgrensning/search/useSearchResult';
import { SearchBar } from '@/components/avgrensning/search/SearchBar';
import { DateFilter } from '@/components/avgrensning/search/DateFilter';
import { stateSearchPanelOpen, stateRecentSearchQueries } from '@/selectors/selectors';
import { DropdownItemData } from './search/dropdown/DropdownItem';
import { DatakatalogMode } from './datakatalog/constants';
import type { DatakatalogWindowLauncher } from './datakatalog/standaloneChannel';
import { useDebounceAndCleanValue } from '@/utils/useDebounceValue';
import { getSeasonalDecoration } from '@/utils/seasonalDecoration';

export interface SearchProps {
    datakatalogOpen: boolean;
    datakatalogMode: DatakatalogMode;
    setDatakatalogMode: (mode: DatakatalogMode) => void;
    setDatakatalogOpen: (open: boolean) => void;
    standaloneTab?: DatakatalogWindowLauncher;
}

export const Search = ({
    datakatalogOpen,
    datakatalogMode,
    setDatakatalogMode,
    setDatakatalogOpen,
    standaloneTab,
}: SearchProps) => {
    const dispatch = useDispatch();
    const dropdownWrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const pendingRequestIdRef = useRef(0);

    const [inputValue, setInputValue] = useState<string>('');
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [hasSearchBarChanged, setHasSearchBarChanged] = useState(false);
    const [dropdownCursor, setDropdownCursor] = useState(-1);
    const [lastInputWasArrowPress, setLastInputWasArrowPress] = useState(false);
    const [pendingSearchText, setPendingSearchText] = useState<string | null>(null);
    const showPanel = useSelector(stateSearchPanelOpen);

    const recentSearchQueries = useSelector(stateRecentSearchQueries);
    const searchText = useDebounceAndCleanValue(inputValue, 500, lastInputWasArrowPress);
    const searchResult = useSearchResult(inputValue);
    const recentSearches = useRecentSearches(recentSearchQueries);

    const noInput = useCallback(() => inputValue.length === 0, [inputValue.length]);

    useEffect(() => {
        dispatch(searchFieldUpdated(searchText));
    }, [dispatch, searchText]);

    const onDropdownItemSelected = useCallback(
        (i?: DropdownItemData) => {
            if (!i || i.disabled) return;

            if (i.keepDropdownOpen) {
                i.onClick?.();
                return;
            }

            setInputValue('');
            dispatch(addSearchQuery(i));
            setDropdownVisible(false);
            inputRef.current?.blur();

            const requestId = pendingRequestIdRef.current + 1;
            pendingRequestIdRef.current = requestId;

            if (i.pendingText) setPendingSearchText(i.pendingText);
            else setPendingSearchText(null);

            Promise.resolve(i.onClick?.())
                .catch(() => {})
                .finally(() => {
                    if (pendingRequestIdRef.current === requestId) {
                        setPendingSearchText(null);
                    }
                });
        },
        [dispatch, setDropdownVisible]
    );

    const { handleKeyDown, handleOnFocus, handleOnClick, handleChange, handleSubmit } =
        useSearchBarFunctions(
            setLastInputWasArrowPress,
            dropdownCursor,
            setDropdownCursor,
            dropdownVisible,
            setDropdownVisible,
            setHasSearchBarChanged,
            inputValue !== '' ? searchResult : recentSearches,
            noInput,
            onDropdownItemSelected,
            dropdownWrapperRef,
            setInputValue
        );

    const handleBackClick = () => {
        setDropdownVisible(false);
        setDatakatalogOpen(false);
        dispatch(setSearchPanelOpen(false));
    };

    const closedClass = showPanel ? '' : ' c-search__closed';

    const currentItems = inputValue !== '' ? searchResult : recentSearches;
    const pendingItems: DropdownItemData[] = pendingSearchText
        ? [
              {
                  title: 'Laster inn',
                  header: true,
                  disabled: true,
              },
              {
                  title: pendingSearchText,
                  subtitle: 'Vennligst vent',
                  disabled: true,
                  icon: 'o-icon-time',
              },
          ]
        : [];
    const searchResultItems = dropdownVisible && hasSearchBarChanged ? currentItems : [];
    const visibleItems = [...pendingItems, ...searchResultItems];
    const isDropdownOpen = visibleItems.length > 0;
    const activeDescendantId =
        isDropdownOpen && dropdownCursor >= 0 ? getOptionId(dropdownCursor) : undefined;
    const resultCount = currentItems.filter(i => !i.header).length;
    const seasonalDecoration = getSeasonalDecoration();

    return (
        <div className="c-search-group">
            <div className={'c-search' + closedClass} ref={dropdownWrapperRef}>
                {seasonalDecoration && (
                    <div
                        className={`c-search__seasonal-decoration c-search__seasonal-decoration--${seasonalDecoration}`}
                        aria-hidden="true"
                    />
                )}
                <SearchBar
                    handleSubmit={handleSubmit}
                    handleKeyDown={handleKeyDown}
                    handleOnFocus={handleOnFocus}
                    handleOnClick={handleOnClick}
                    handleChange={handleChange}
                    datakatalogOpen={datakatalogOpen}
                    datakatalogMode={datakatalogMode}
                    setDatakatalogMode={setDatakatalogMode}
                    setDatakatalogOpen={setDatakatalogOpen}
                    standaloneTab={standaloneTab}
                    inputValue={inputValue}
                    dropdownExpanded={isDropdownOpen}
                    activeDescendantId={activeDescendantId}
                    listboxId={SEARCH_LISTBOX_ID}
                    inputRef={inputRef}
                />
                {isDropdownOpen && (
                    <SearchContainer
                        items={visibleItems}
                        focusIndex={dropdownCursor}
                        onDropdownItemSelected={onDropdownItemSelected}
                    />
                )}
                <div aria-live="polite" aria-atomic="true" className="sr-only">
                    {isDropdownOpen && `${resultCount} søkeresultater tilgjengelig`}
                </div>
                <div className="t-visible-mobile">
                    <button
                        type="button"
                        onClick={handleBackClick}
                        className="o-btn o-btn--orange-bg o-btn--phone-back"
                    >
                        Tilbake
                    </button>
                </div>
            </div>
            <DateFilter className={showPanel ? '' : 'c-date-selector__closed'} />
        </div>
    );
};
